/**
 * DuePay Balance Bar Component
 * Gerencia a barra de saldo DuePay e integração com o carrinho
 */
class DuepayBalanceBar extends HTMLElement {
  constructor() {
    super();
    this.storageKeys = { uniforme: 'duepay_balance_uniforme', material: 'duepay_balance_material' };
    this.balances = { uniforme: null, material: null };
    this.updateTimeout = null;
    this.observers = [];
  }

  connectedCallback() {
    this.loadBalances();
    this.bindEvents();
    // Verifica se está vazio e adiciona classe inicialmente
    const hasUniforme = this.balances.uniforme !== null && this.balances.uniforme > 0;
    const hasMaterial = this.balances.material !== null && this.balances.material > 0;
    if (!hasUniforme && !hasMaterial) {
      this.classList.add('duepay-balance-bar--empty');
    }
    this.updateBarText(); // Mostra texto inicial imediatamente
    // Atualiza com dados do carrinho sem debounce na primeira vez
    this.updateCartDisplay(true);
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
    this.observers.forEach(obs => obs.disconnect());
    if (this.updateTimeout) clearTimeout(this.updateTimeout);
  }

  // ========== LOCALSTORAGE ==========

  loadBalances() {
    ['uniforme', 'material'].forEach(type => {
      const value = localStorage.getItem(this.storageKeys[type]);
      this.balances[type] = value ? parseFloat(value) : null;
      
      const input = this.querySelector(`[data-balance-input="${type}"]`);
      if (input && this.balances[type] > 0) {
        input.value = this.balances[type].toFixed(2).replace('.', ',');
      }
    });
  }

  saveBalances() {
    const values = ['uniforme', 'material'].map(type => {
      const input = document.querySelector(`[data-balance-input="${type}"]`);
      const value = input?.value?.trim();
      return value ? this.parseBrazilianNumber(value) : null;
    });

    const [uniformeBalance, materialBalance] = values;

    // Validações
    if (!uniformeBalance && !materialBalance) {
      this.showError('Por favor, preencha pelo menos um dos campos de saldo.');
      return false;
    }

    if ((uniformeBalance !== null && uniformeBalance < 0) || (materialBalance !== null && materialBalance < 0)) {
      this.showError('Os valores de saldo não podem ser negativos.');
      return false;
    }

    // Salva no localStorage
    ['uniforme', 'material'].forEach((type, i) => {
      const balance = values[i];
      if (balance !== null) {
        localStorage.setItem(this.storageKeys[type], balance.toString());
        this.balances[type] = balance;
      } else {
        localStorage.removeItem(this.storageKeys[type]);
        this.balances[type] = null;
      }
    });

    return true;
  }

  parseBrazilianNumber(value) {
    if (!value) return null;
    const cleaned = value.toString().trim().replace(/\s/g, '').replace(/\./g, '').replace(',', '.');
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? null : parsed;
  }

  // ========== EXIBIÇÃO ==========

  formatMoney(value) {
    if (value === null || value === undefined) return 'R$ 0,00';
    return `R$ ${value.toFixed(2).replace('.', ',')}`;
  }

  formatMoneyWithHighlight(value, isExceeded = false) {
    const formatted = this.formatMoney(value);
    const exceededClass = isExceeded ? ' duepay-balance-bar__value--exceeded' : '';
    return `<span class="duepay-balance-bar__value${exceededClass}">${formatted}</span>`;
  }

  updateBarText(balanceInfo = null) {
    const textElement = this.querySelector('[data-balance-text]');
    if (!textElement) return;

    const hasUniforme = this.balances.uniforme > 0;
    const hasMaterial = this.balances.material > 0;

    if (!hasUniforme && !hasMaterial) {
      textElement.textContent = 'INSIRA SEU SALDO DUEPAY AQUI!';
      // Adiciona classe para indicar que está vazio
      this.classList.add('duepay-balance-bar--empty');
      return;
    }

    // Remove classe quando há saldo
    this.classList.remove('duepay-balance-bar--empty');

    const textParts = [];
    const cartType = balanceInfo?.type;
    const isExceeded = balanceInfo?.isExceeded || false;

    if (cartType) {
      // Há produtos no carrinho - mostra saldo disponível (total - carrinho)
      const balanceKey = cartType.toLowerCase();
      const totalBalance = this.balances[balanceKey];
      
      if (totalBalance > 0) {
        const available = balanceInfo.available !== null ? balanceInfo.available : totalBalance;
        
        if (isExceeded) {
          // Valor excedido - mostra mensagem e valor negativo
          textParts.push(`VOCÊ EXCEDEU O VALOR INFORMADO: ${this.formatMoneyWithHighlight(available, true)}`);
        } else {
          textParts.push(`VOCÊ TÊM SALDO ${cartType} DE: ${this.formatMoneyWithHighlight(available)}`);
        }
      }

      // Mostra o outro saldo se existir (sem subtração)
      if (cartType === 'UNIFORME' && hasMaterial) {
        textParts.push(`VOCÊ TÊM SALDO MATERIAL DE: ${this.formatMoneyWithHighlight(this.balances.material)}`);
      } else if (cartType === 'MATERIAL' && hasUniforme) {
        textParts.push(`VOCÊ TÊM SALDO UNIFORME DE: ${this.formatMoneyWithHighlight(this.balances.uniforme)}`);
      }
    } else {
      // Sem produtos no carrinho - mostra saldos totais
      if (hasUniforme) textParts.push(`VOCÊ TÊM SALDO UNIFORME DE: ${this.formatMoneyWithHighlight(this.balances.uniforme)}`);
      if (hasMaterial) textParts.push(`VOCÊ TÊM SALDO MATERIAL DE: ${this.formatMoneyWithHighlight(this.balances.material)}`);
    }

    textElement.innerHTML = textParts.join(' | ');
  }

  // ========== CARRINHO ==========

  async fetchCartData() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) throw new Error('Falha ao buscar carrinho');
      
      const cart = await response.json();
      const firstItem = cart.items?.[0];
      const productType = firstItem?.properties?._product_type?.toUpperCase();
      const validType = (productType === 'MATERIAL' || productType === 'UNIFORME') ? productType : null;

      return {
        productType: validType,
        cartTotal: cart.total_price ? cart.total_price / 100 : 0,
        cart
      };
    } catch (error) {
      console.error('Erro ao buscar dados do carrinho:', error);
      // Fallback: tenta pegar do DOM
      const totalEl = document.querySelector('.totals__total-value');
      const cartTotal = totalEl ? parseFloat(totalEl.textContent.replace(/[^\d,]/g, '').replace(',', '.')) || 0 : 0;
      return { productType: null, cartTotal, cart: null };
    }
  }

  async calculateAvailableBalance() {
    const { productType, cartTotal } = await this.fetchCartData();
    
    if (!productType) {
      return {
        uniforme: this.balances.uniforme,
        material: this.balances.material,
        type: null
      };
    }

    const balanceKey = productType.toLowerCase();
    const totalBalance = this.balances[balanceKey];

    if (totalBalance === null) {
      return {
        uniforme: this.balances.uniforme,
        material: this.balances.material,
        type: productType,
        available: null
      };
    }

    const available = totalBalance - cartTotal;
    const isExceeded = available < 0;

    return {
      uniforme: this.balances.uniforme,
      material: this.balances.material,
      type: productType,
      available: available,
      used: cartTotal,
      total: totalBalance,
      isExceeded: isExceeded
    };
  }

  updateCartDisplay(immediate = false) {
    if (this.updateTimeout) clearTimeout(this.updateTimeout);

    const update = async () => {
      const balanceInfo = await this.calculateAvailableBalance();
      this.updateBarText(balanceInfo);

      document.dispatchEvent(new CustomEvent('duepay:balance-updated', { detail: balanceInfo }));

      const cartBalanceEl = document.querySelector('cart-drawer-balance, .cart-balance-display');
      if (cartBalanceEl?.updateBalance) cartBalanceEl.updateBalance(balanceInfo);
    };

    // Primeira chamada (carregamento) executa imediatamente, outras com debounce
    if (immediate) {
      update();
    } else {
      this.updateTimeout = setTimeout(update, 100);
    }
  }

  // ========== EVENTOS ==========

  bindEvents() {
    // Formulário
    const form = document.querySelector('#DuepayBalanceForm');
    if (form) {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.handleSave();
      });
    }

    // Modal
    const modal = this.querySelector('#PopupModal-duepay-balance');
    if (modal) {
      modal.addEventListener('modalClosed', () => this.updateCartDisplay());
    }

    // Sistema pub/sub do tema
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.cartData) this.updateCartDisplay();
      });
    }

    // Eventos customizados
    ['cart:updated', 'cart:open', 'cart:item-added', 'cart:item-removed'].forEach(eventName => {
      document.addEventListener(eventName, () => this.updateCartDisplay());
    });

    // Observers
    this.setupObservers();
  }

  setupObservers() {
    // Observer do cart-drawer
    const findCartDrawer = () => {
      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer) {
        setTimeout(findCartDrawer, 500);
        return;
      }

      // Observa abertura do drawer
      const drawerObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && cartDrawer.classList.contains('active')) {
            setTimeout(() => this.updateCartDisplay(), 400);
          }
        });
      });

      drawerObserver.observe(cartDrawer, { attributes: true, attributeFilter: ['class'] });
      this.observers.push(drawerObserver);

      // Observa mudanças no conteúdo do drawer
      const content = cartDrawer.querySelector('.drawer__inner');
      if (content) {
        const contentObserver = new MutationObserver((mutations) => {
          const shouldUpdate = mutations.some(mutation => {
            const target = mutation.target;
            return target.classList?.contains('totals__total-value') ||
                   target.closest('.cart-items') ||
                   target.closest('.drawer__contents');
          });
          if (shouldUpdate) this.updateCartDisplay();
        });

        contentObserver.observe(content, { childList: true, subtree: true, characterData: true });
        this.observers.push(contentObserver);
      }
    };

    findCartDrawer();

    // Observer do total do carrinho
    const totalEl = document.querySelector('.totals__total-value');
    if (totalEl) {
      const totalObserver = new MutationObserver(() => this.updateCartDisplay());
      totalObserver.observe(totalEl, { childList: true, characterData: true, subtree: true });
      this.observers.push(totalObserver);
    }
  }

  // ========== AÇÕES ==========

  async handleSave() {
    if (!this.saveBalances()) return;

    const modal = document.querySelector('#PopupModal-duepay-balance');
    if (modal && modal.hide) {
      modal.hide();
    }

    await this.updateCartDisplay();
    this.showSuccess('Saldo salvo com sucesso!');
  }

  showError(message) {
    const existingError = document.querySelector('.duepay-balance-form__error');
    if (existingError) existingError.remove();

    const errorEl = document.createElement('div');
    errorEl.className = 'duepay-balance-form__error';
    errorEl.textContent = message;
    errorEl.setAttribute('role', 'alert');

    const form = document.querySelector('#DuepayBalanceForm');
    if (form) {
      form.insertBefore(errorEl, form.firstChild);
      errorEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  showSuccess(message) {
    console.log(message);
  }
}

if (!customElements.get('duepay-balance-bar')) {
  customElements.define('duepay-balance-bar', DuepayBalanceBar);
}
